import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Eye, EyeOff, FlaskConical, RefreshCw, Webhook } from "lucide-react";
import { api, money, shopDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const OUTCOME_STYLES = {
  success: "bg-[#DCFCE7] text-[#166534]",
  failed: "bg-[#FEE2E2] text-[#B91C1C]",
  unknown: "bg-[#FEF3C7] text-[#92400E]",
};

// What each knock on the webhook means in plain English, for the arrival log.
const ATTEMPT_STYLES = {
  accepted: "bg-[#DCFCE7] text-[#166534]",
  ping_ok: "bg-[#DBEAFE] text-[#1E40AF]",
  bad_key: "bg-[#FEE2E2] text-[#B91C1C]",
  bad_signature: "bg-[#FEF3C7] text-[#92400E]",
  too_large: "bg-[#FEF3C7] text-[#92400E]",
};

const ATTEMPT_LABELS = {
  accepted: "Accepted",
  ping_ok: "Reachability check",
  bad_key: "Wrong key",
  bad_signature: "Bad signature",
  too_large: "Too large",
};

const CopyField = ({ label, value, secret = false, testid }) => {
  const [shown, setShown] = useState(!secret);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value || "");
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="flex-1 break-all text-xs" data-testid={testid}>
          {shown ? value || "—" : "•".repeat(40)}
        </code>
        {secret && (
          <button
            onClick={() => setShown((s) => !s)}
            className="text-muted-foreground hover:text-foreground"
            data-testid={`${testid}-toggle`}
          >
            {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        <button
          onClick={copy}
          className="text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-copy`}
        >
          {copied ? <Check className="h-4 w-4 text-[#166534]" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default function KuberIntegration() {
  const { can } = useAuth();
  const [info, setInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [arrivals, setArrivals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testAmount, setTestAmount] = useState("");
  const [testStatus, setTestStatus] = useState("SUCCESS");
  const [testResult, setTestResult] = useState("");
  const isPreview = (process.env.REACT_APP_BACKEND_URL || "").includes(".preview.");

  const load = useCallback(() => {
    if (!can("users:manage")) return;
    api.get("/integrations/kuber").then(({ data }) => setInfo(data)).catch(() => {});
    api.get("/integrations/kuber/events").then(({ data }) => setEvents(data)).catch(() => {});
    api
      .get("/integrations/kuber/attempts")
      .then(({ data }) => setArrivals(data))
      .catch(() => {});
  }, [can]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateConfirm, setRotateConfirm] = useState("");

  const rotate = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/kuber/rotate", { confirm: rotateConfirm.trim() });
      toast.success("New URL and secret generated — send them to Kuber now");
      setRotateOpen(false);
      setRotateConfirm("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rotate the credentials");
    } finally {
      setBusy(false);
    }
  };

  // One pack per environment, so Kuber's dev is wired to our practice site and their
  // production to our live site. Sending the pack verbatim is what stops the two being
  // crossed over, which answers "Not found" on every push.
  const copyPack = async () => {
    const live = info?.environment === "live";
    const pack = [
      `Kuber webhook details — for your ${live ? "PRODUCTION" : "DEV / TEST"} environment ONLY`,
      "",
      `Use these in your ${live ? "production" : "dev/test"} environment and nowhere else.`,
      live
        ? "Do NOT use these in your dev environment — dev payments would post against real customer invoices in our live shop."
        : "These point at our practice shop. Nothing here reaches real customers or real invoices.",
      "",
      `Method:            POST`,
      `URL:               ${info?.webhook_url || ""}`,
      `Signing secret:    ${info?.signing_secret || ""}`,
      `Signature header:  X-Kuber-Signature`,
      `Signature value:   hex HMAC-SHA256 of the raw request body, using the secret above`,
      "",
      "Required fields:",
      "  Invoice Number — our invoice number, exactly as printed. Not a placeholder.",
      "  Amount         — the full balance owing on that invoice, including any handling fee.",
      "",
      "Check it before you send anything:",
      `  curl ${info?.webhook_url || ""}`,
      '  A GET should return {"ok":true}. If you get {"detail":"Not found"} the key is',
      "  wrong or is from our other environment. 401 means the signature is wrong.",
    ].join("\n");
    await navigator.clipboard.writeText(pack);
    // Recorded so the rotate warning can say when Kuber were last sent these details.
    api.post("/integrations/kuber/handover-logged").then(load).catch(() => {});
    toast.success(
      `Handover pack copied — for Kuber's ${live ? "PRODUCTION" : "DEV"} environment`);
  };

  const sendTest = async () => {
    setBusy(true);
    setTestResult("");
    try {
      const { data } = await api.post("/integrations/kuber/test-webhook", {
        invoice_number: testNumber.trim(),
        amount: Number(testAmount || 0),
        status: testStatus,
      });
      setTestResult(JSON.stringify(data, null, 2));
      if (data?.result?.matched_invoice) {
        toast.success(`Matched invoice #${data.result.matched_invoice}`);
      } else {
        toast.message("Push recorded but not matched — see the result below");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the test push");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="kuber-integration-page">
      {!can("users:manage") && (
        <div className="border border-[#B91C1C] bg-[#FEF2F2] p-6" data-testid="kuber-denied">
          <h1 className="text-xl font-bold text-[#B91C1C]">Owner access only</h1>
          <p className="mt-1 text-sm text-[#7F1D1D]">
            The Kuber webhook URL and signing secret are payment credentials, so only an
            administrator can view this page.
          </p>
        </div>
      )}
      {can("users:manage") && (
        <>
      <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Integrations</p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Kuber webhook</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Give Kuber the URL and secret below. They post here the moment a payment succeeds or
        fails, and every push is recorded. A payment that carries one of our invoice numbers
        and clears the balance exactly is posted to that invoice; anything else waits here
        for you to match by hand.
      </p>

      <div className="mt-4 border border-border bg-[#FBF8F2] p-4 text-sm" data-testid="kuber-brief">
        <p className="font-semibold">What Kuber need to send</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>
            <span className="font-semibold">Their dev talks to our practice site; their
            production talks to our live site.</span> Each of our environments has its own
            key, so a pair from one never works on the other — the wrong key answers{" "}
            <code>{"{ detail: 'Not found' }"}</code>. Use the button below to copy a
            handover pack that says which of their environments it is for.
          </li>
          <li>
            Header <code className="font-semibold">X-Kuber-Signature</code>: hex
            HMAC-SHA256 of the raw body using the secret below. Without it they get a 401.
          </li>
          <li>
            Our invoice number in <code>Invoice Number</code> — not a placeholder. Field
            names in any style are understood (<code>Invoice Number</code>,{" "}
            <code>invoice_number</code>, <code>merchantReference</code>).
          </li>
          <li>
            <code>Amount</code> must be the full balance owing on that invoice, including
            the handling fee, or it is held here rather than posted.
          </li>
        </ul>
      </div>

      {info?.environment === "preview" ? (
        <div
          className="mt-4 border-l-4 border-[#B91C1C] bg-[#FEF2F2] p-4 text-sm"
          data-testid="kuber-env-warning"
        >
          <p className="font-semibold text-[#B91C1C]">
            PRACTICE site — these details belong in Kuber's DEV environment only
          </p>
          <p className="mt-1 text-[#7C2D12]">
            Give the pair below to Kuber for their <span className="font-semibold">dev /
            test</span> environment. Their production must use the pair from our live site
            instead. Crossing the two over is what answers{" "}
            <code>{"{ detail: 'Not found' }"}</code> on every push.
          </p>
        </div>
      ) : (
        <div
          className="mt-4 border-l-4 border-[#166534] bg-[#F0FDF4] p-4 text-sm"
          data-testid="kuber-env-live"
        >
          <p className="font-semibold text-[#166534]">
            LIVE site — these details belong in Kuber's PRODUCTION environment only
          </p>
          <p className="mt-1 text-[#14532D]">
            Never give this pair to their dev environment: a dev test payment would post
            against a real customer invoice and mark it paid. Their dev gets the pair from
            our practice site.
          </p>
        </div>
      )}

      <div className="mt-6 grid gap-3 lg:grid-cols-2">
        <CopyField
          label={`Webhook URL (POST) — for Kuber ${
            info?.environment === "preview" ? "DEV" : "PRODUCTION"}`}
          value={info?.webhook_url}
          testid="kuber-url"
        />
        <CopyField
          label="Signing secret"
          value={info?.signing_secret}
          secret
          testid="kuber-secret"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 border border-[#C5A059] bg-[#FBF8F2] p-3 text-xs">
        <span className="text-muted-foreground">
          Copies a ready-to-send block with the URL, secret, signature rules and required
          fields — labelled for Kuber's{" "}
          <span className="font-semibold">
            {info?.environment === "preview" ? "dev" : "production"}
          </span>{" "}
          environment.
        </span>
        <Button
          variant="outline"
          className="ml-auto rounded-none"
          onClick={copyPack}
          disabled={!info}
          data-testid="kuber-copy-pack"
        >
          <Copy className="mr-1.5 h-4 w-4" />
          Copy handover pack for Kuber{" "}
          {info?.environment === "preview" ? "DEV" : "PROD"}
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 border border-border p-3 text-xs text-muted-foreground">
        <Webhook className="h-4 w-4 text-[#C5A059]" />
        <span>
          Signature header <code className="font-semibold">X-Kuber-Signature</code> — hex
          HMAC-SHA256 of the raw body using the secret. Duplicate deliveries of the same event
          id are ignored automatically.
        </span>
        <span data-testid="kuber-event-count">
          {info?.events_received ?? 0} received so far
        </span>
        <span data-testid="kuber-key-age">
          Key {info?.rotated_at ? "rotated" : "created"}{" "}
          {shopDateTime(info?.rotated_at || info?.created_at) || "—"}
          {info?.rotated_at && (
            <span className="font-semibold text-[#B91C1C]">
              {" "}— anyone given the old URL before this must be re-sent it
            </span>
          )}
        </span>
        <Button
          variant="outline"
          className="ml-auto rounded-none"
          onClick={() => setRotateOpen(true)}
          disabled={busy}
          data-testid="kuber-rotate-button"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" /> Rotate URL & secret
        </Button>
      </div>

      <AlertDialog open={rotateOpen} onOpenChange={setRotateOpen}>
        <AlertDialogContent className="rounded-none" data-testid="kuber-rotate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Rotate the {info?.environment === "live" ? "LIVE" : "practice"} webhook
              URL and secret?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This replaces the key and secret immediately. Anyone still using the
                  old pair gets <code>{"{ detail: 'Not found' }"}</code> on every push
                  until you send them the new one.
                </p>
                <div className="border border-border bg-[#FBF8F2] p-3 text-xs">
                  <div data-testid="rotate-key-age">
                    Current key {info?.rotated_at ? "rotated" : "created"}:{" "}
                    <span className="font-semibold">
                      {shopDateTime(info?.rotated_at || info?.created_at) || "—"}
                    </span>
                  </div>
                  <div className="mt-1" data-testid="rotate-handover">
                    {info?.handover_copied_at ? (
                      <>
                        Last sent to Kuber:{" "}
                        <span className="font-semibold">
                          {shopDateTime(info.handover_copied_at)}
                          {info.handover_copied_by ? ` by ${info.handover_copied_by}` : ""}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        No handover pack has been copied for this pair yet.
                      </span>
                    )}
                  </div>
                  <div className="mt-1" data-testid="rotate-arrivals">
                    Pushes received on this pair:{" "}
                    <span className="font-semibold">{arrivals?.total ?? 0}</span>
                  </div>
                </div>
                {info?.environment === "live" && (
                  <div
                    className="border-l-4 border-[#B91C1C] bg-[#FEF2F2] p-3 text-xs font-semibold text-[#B91C1C]"
                    data-testid="rotate-live-warning"
                  >
                    {info?.handover_copied_at
                      ? "Kuber are relying on this pair in production. Rotating will stop real customer payments from being recorded until they are given the new details."
                      : "This is the live site. Rotating will stop any production pushes until Kuber are given the new details."}
                  </div>
                )}
                {info?.environment === "live" && (
                  <label className="block text-xs">
                    Type <span className="font-bold">ROTATE</span> to confirm
                    <input
                      value={rotateConfirm}
                      onChange={(e) => setRotateConfirm(e.target.value)}
                      className="mt-1 block w-full border border-border bg-white px-2 py-1.5 text-sm"
                      placeholder="ROTATE"
                      data-testid="rotate-confirm-input"
                    />
                  </label>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-none"
              onClick={() => setRotateConfirm("")}
              data-testid="rotate-cancel"
            >
              Keep the current pair
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-[#B91C1C] hover:bg-[#991B1B]"
              onClick={(e) => {
                e.preventDefault();
                rotate();
              }}
              disabled={
                busy ||
                (info?.environment === "live" &&
                  rotateConfirm.trim().toUpperCase() !== "ROTATE")
              }
              data-testid="rotate-confirm-button"
            >
              Rotate now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isPreview && (
        <div
          className="mt-6 border border-[#C5A059] bg-[#FFFBEB] p-4"
          data-testid="kuber-test-panel"
        >
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-[#B45309]" />
            <h2 className="text-lg font-semibold">Send a test push (preview only)</h2>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-[#7C2D12]">
            Fires a correctly signed fake Kuber payment at our own webhook, exactly as Kuber
            will once they go live. Type one of this sandbox's invoice numbers and the amount
            still owing on it — if everything is wired up, the invoice is marked paid and the
            push appears below as matched. This panel never appears in the live app, and will
            be removed once Kuber's real webhook has been proven.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Our invoice number
              <input
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                className="mt-1 block w-44 border border-border bg-white px-2 py-1.5 text-sm"
                placeholder="211258"
                data-testid="kuber-test-invoice"
              />
            </label>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Amount paid
              <input
                value={testAmount}
                onChange={(e) => setTestAmount(e.target.value)}
                className="mt-1 block w-32 border border-border bg-white px-2 py-1.5 text-sm"
                placeholder="0.00"
                data-testid="kuber-test-amount"
              />
            </label>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Outcome
              <select
                value={testStatus}
                onChange={(e) => setTestStatus(e.target.value)}
                className="mt-1 block w-36 border border-border bg-white px-2 py-1.5 text-sm"
                data-testid="kuber-test-status"
              >
                <option value="SUCCESS">Successful</option>
                <option value="DECLINED">Declined</option>
              </select>
            </label>
            <Button
              className="rounded-none"
              onClick={sendTest}
              disabled={busy || !testNumber.trim()}
              data-testid="kuber-test-send"
            >
              Send test push
            </Button>
          </div>
          {testResult && (
            <pre
              className="mt-3 max-h-60 overflow-auto border border-border bg-white p-3 text-[11px]"
              data-testid="kuber-test-result"
            >
              {testResult}
            </pre>
          )}
        </div>
      )}

      <h2 className="mt-8 text-lg font-semibold">Did Kuber's request reach us?</h2>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Every knock on our webhook is listed here, accepted or not — so when Kuber say
        "we sent it", you can see whether it arrived and, if it bounced, exactly why. Only
        the last 6 characters of the key they used are stored, which is enough to tell
        whether they used this environment's pair. Rows clear themselves after a month.
      </p>

      <div
        className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 border border-border p-3 text-xs"
        data-testid="kuber-arrivals-summary"
      >
        <span>
          Last arrival:{" "}
          <span className="font-semibold">
            {arrivals?.last_attempt_at ? shopDateTime(arrivals.last_attempt_at) : "never"}
          </span>
        </span>
        <span>
          Total arrivals: <span className="font-semibold">{arrivals?.total ?? 0}</span>
        </span>
        <span>
          Bounced:{" "}
          <span
            className={`font-semibold ${
              arrivals?.rejected ? "text-[#B91C1C]" : ""
            }`}
          >
            {arrivals?.rejected ?? 0}
          </span>
        </span>
        <span className="text-muted-foreground">
          This environment's key ends{" "}
          <code className="font-semibold">{arrivals?.expected_key_tail || "—"}</code>
        </span>
      </div>

      <div className="mt-2 overflow-x-auto border border-border">
        <table className="w-full text-sm" data-testid="kuber-arrivals-table">
          <thead className="bg-[#0F1419] text-left text-xs uppercase tracking-wider text-white">
            <tr>
              <th className="px-3 py-2.5">When</th>
              <th className="px-3 py-2.5">Result</th>
              <th className="px-3 py-2.5">Key used</th>
              <th className="px-3 py-2.5">Address hit</th>
              <th className="px-3 py-2.5">From IP</th>
              <th className="px-3 py-2.5">What it means</th>
            </tr>
          </thead>
          <tbody>
            {(arrivals?.attempts || []).length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                  data-testid="kuber-arrivals-empty"
                >
                  Nothing has reached our webhook yet. If Kuber believe they have sent
                  something and this stays empty, their request is not arriving at this
                  address at all — check the domain in the URL they are using.
                </td>
              </tr>
            )}
            {(arrivals?.attempts || []).map((a) => (
              <tr
                key={a.id}
                className="border-t border-border"
                data-testid={`kuber-arrival-${a.id}`}
              >
                <td className="whitespace-nowrap px-3 py-2.5">{shopDateTime(a.at)}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold uppercase ${
                      ATTEMPT_STYLES[a.outcome] || OUTCOME_STYLES.unknown
                    }`}
                  >
                    {ATTEMPT_LABELS[a.outcome] || a.outcome}
                  </span>
                  <div className="mt-0.5 text-[10px] uppercase text-muted-foreground">
                    {a.method}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5">
                  <code>…{a.key_tail || "—"}</code>
                  {arrivals?.expected_key_tail && a.key_tail !== arrivals.expected_key_tail && (
                    <div className="text-[10px] font-semibold text-[#B91C1C]">
                      not this environment's key
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs">{a.host || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-xs">{a.ip || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                  {a.note || "—"}
                  {a.outcome === "bad_signature" && !a.signature_present && (
                    <div className="font-semibold text-[#92400E]">
                      No signature header was sent
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 text-lg font-semibold">Recent pushes</h2>
      <div className="mt-2 overflow-x-auto border border-border">
        <table className="w-full text-sm" data-testid="kuber-events-table">
          <thead className="bg-[#0F1419] text-left text-xs uppercase tracking-wider text-white">
            <tr>
              <th className="px-3 py-2.5">Received</th>
              <th className="px-3 py-2.5">Outcome</th>
              <th className="px-3 py-2.5">Amount</th>
              <th className="px-3 py-2.5">Method</th>
              <th className="px-3 py-2.5">Their reference</th>
              <th className="px-3 py-2.5">Our invoice</th>
              <th className="px-3 py-2.5">Signed</th>
              <th className="px-3 py-2.5">Note</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                  data-testid="kuber-events-empty"
                >
                  Nothing received yet. Once Kuber posts here, every success and failure shows
                  up on this list within seconds.
                </td>
              </tr>
            )}
            {events.map((e) => (
              <tr key={e.id} className="border-t border-border" data-testid={`kuber-event-${e.id}`}>
                <td className="whitespace-nowrap px-3 py-2.5">
                  {shopDateTime(e.received_at)}
                  {e.paid_at && (
                    <div
                      className="text-[10px] text-muted-foreground"
                      data-testid={`kuber-event-their-time-${e.id}`}
                    >
                      their clock {shopDateTime(e.paid_at)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold uppercase ${
                      OUTCOME_STYLES[e.outcome] || OUTCOME_STYLES.unknown
                    }`}
                  >
                    {e.outcome}
                  </span>
                  {e.status_raw && (
                    <div className="mt-0.5 text-[10px] uppercase text-muted-foreground">
                      {e.status_raw}
                    </div>
                  )}
                </td>
                <td className="tabular px-3 py-2.5">
                  {e.amount == null ? "—" : money(e.amount)}
                  {e.fee != null && (
                    <div className="text-[10px] text-muted-foreground">fee {money(e.fee)}</div>
                  )}
                </td>
                <td className="px-3 py-2.5">{e.method || "—"}</td>
                <td className="px-3 py-2.5">
                  {e.reference || "—"}
                  {e.transaction_id && (
                    <div className="text-[10px] text-muted-foreground">{e.transaction_id}</div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {e.matched_doc_id ? (
                    <a
                      href={`/documents/${e.matched_doc_id}`}
                      className="underline"
                      data-testid={`kuber-event-doc-${e.id}`}
                    >
                      #{e.matched_doc_number}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">not matched</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {e.signature_verified ? (
                    <span className="text-[#166534]">verified</span>
                  ) : (
                    <span className="text-[#B91C1C]">unsigned</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                  {e.hold_reason ? (
                    <span className="text-[#B45309]" data-testid={`kuber-event-hold-${e.id}`}>
                      Held{e.held_doc_number ? ` (#${e.held_doc_number})` : ""} — {e.hold_reason}
                    </span>
                  ) : (
                    e.failure_reason || e.event_type || "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}
